import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import BlogContext from "../../Context/BlogContext";
import { getServicePageByCluster } from "../../data/servicePages";
import {
  getBlogContentSections,
  getBlogByTitle,
  getReadingTimeMinutes,
  parseHtml,
} from "../../Services/blog.service";
import { formatDate } from "../../Services/common-util.service";
import GetHelpForm from "../Shared/GetHelpForm";
import Seo from "../../seo/Seo";
import {
  SEO_CONFIG,
  buildAbsoluteUrl,
  buildCanonicalUrl,
  createBlogDescription,
  createBreadcrumbSchema,
} from "../../seo/utils";
import AssignmentHelpHowItWorks from "../Shared/AssignmentHelpHowItWorks";
import AssignmentSupportCta from "../Shared/AssignmentSupportCta";
import BlogPageSitelinks from "./BlogPageSitelinks";
import ReviewsTrustBar from "../Reviews/ReviewsTrustBar";
import { getBlogPath, getSlugFromPathname } from "../../utils/routes";
import "./BlogViewer.css";

const BLOG_AUTHOR_NAME = "SubjectBuddy Editorial Team";

function BlogViewer() {
  const { blogs } = useContext(BlogContext);
  const { title: routeTitle } = useParams();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const desktopMediaQuery = useMediaQuery(
    `(min-width: ${theme.breakpoints.md})`
  );
  const title = routeTitle || getSlugFromPathname(pathname);

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(hash.replace(/^#/, ""));

        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });

      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [hash, title]);

  const blogByTitle = getBlogByTitle(blogs, title);
  const articlePath = blogByTitle ? getBlogPath(blogByTitle.slug) : "/blogs";
  const articleDescription = createBlogDescription(blogByTitle);
  const articleTitle = blogByTitle
    ? blogByTitle.metaTitle || `${blogByTitle.title} | SubjectBuddy Blog`
    : "Blog Article | SubjectBuddy";
  const [hasHeroImageError, setHasHeroImageError] = useState(false);
  const shouldShowHeroImage = Boolean(blogByTitle?.image && !hasHeroImageError);
  const articleImage = blogByTitle?.image || SEO_CONFIG.defaultImage;
  const articleImageAlt =
    blogByTitle?.imageAlt ||
    (blogByTitle ? `${blogByTitle.title} cover image` : SEO_CONFIG.defaultImageAlt);

  useEffect(() => {
    setHasHeroImageError(false);
  }, [blogByTitle?.image, blogByTitle?.slug]);

  const articleSchema = blogByTitle
    ? {
        "@context": "https://schema.org",
        "@type": blogByTitle.schemaType || "BlogPosting",
        headline: blogByTitle.title,
        description: articleDescription,
        url: buildCanonicalUrl(articlePath),
        image: buildAbsoluteUrl(articleImage),
        datePublished: blogByTitle.publishedOn,
        dateModified: blogByTitle.publishedOn,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": buildCanonicalUrl(articlePath),
        },
        keywords: blogByTitle.tags,
        articleSection: blogByTitle.cluster,
        author: {
          "@type": "Organization",
          name: SEO_CONFIG.siteName,
        },
        publisher: {
          "@type": "Organization",
          name: SEO_CONFIG.siteName,
          logo: {
            "@type": "ImageObject",
            url: SEO_CONFIG.defaultImage,
          },
        },
      }
    : undefined;
  const additionalArticleSchemas = blogByTitle?.schema
    ? Array.isArray(blogByTitle.schema)
      ? blogByTitle.schema
      : [blogByTitle.schema]
    : [];
  const relatedBlogs = blogByTitle?.relatedSlugs?.length
    ? blogs.filter((blog) => blogByTitle.relatedSlugs?.includes(blog.slug))
    : blogs
        .filter(
          (blog) =>
            blog.slug !== blogByTitle?.slug &&
            blog.cluster === blogByTitle?.cluster
        )
        .slice(0, 3);
  const relatedService = getServicePageByCluster(blogByTitle?.cluster);
  const readingTime = getReadingTimeMinutes(blogByTitle?.body ?? "");
  const publishedLabel = formatDate(blogByTitle?.publishedOn);
  const tagPreview = blogByTitle?.tags?.slice(0, 4) ?? [];
  const articleContent = getBlogContentSections(
    blogByTitle?.body ?? "",
    blogByTitle?.title,
    blogByTitle?.tocMaxItems,
  );

  return (
    <Container
      fluid
      className="medium-article-shell"
      px={desktopMediaQuery ? "xl" : "md"}
    >
      <Seo
        title={articleTitle}
        description={articleDescription}
        path={articlePath}
        keywords={blogByTitle?.tags || []}
        image={articleImage}
        imageAlt={articleImageAlt}
        type="article"
        publishedTime={blogByTitle?.publishedOn}
        modifiedTime={blogByTitle?.publishedOn}
        schema={
          blogByTitle
            ? [
                articleSchema as Record<string, unknown>,
                ...additionalArticleSchemas,
                createBreadcrumbSchema([
                  { name: "Home", path: "/" },
                  { name: "Blogs", path: "/blogs" },
                  { name: blogByTitle.title, path: articlePath },
                ]),
              ]
            : undefined
        }
        noIndex={!blogByTitle}
      />

      <div className="medium-article-layout">
        <article className="medium-article-main">
          <div className="medium-article-column">
            <Button
              variant="subtle"
              color="dark"
              leftSection={<IconArrowLeft size={16} />}
              className="medium-back-button"
              onClick={() => navigate("/blogs")}
            >
              Back to blogs
            </Button>

            <nav
              className="medium-article-breadcrumbs"
              aria-label="Breadcrumb"
            >
              <ol>
                <li>
                  <Link to="/">Home</Link>
                </li>
                <li>
                  <Link to="/blogs">Student guides</Link>
                </li>
                <li aria-current="page">
                  {blogByTitle?.title ?? "Blog Article"}
                </li>
              </ol>
            </nav>

            <header className="medium-article-header">
              {blogByTitle?.cluster ? (
                <Text className="medium-article-kicker">
                  {blogByTitle.cluster}
                </Text>
              ) : null}

              <Title order={1} className="medium-article-title">
                {blogByTitle?.title ?? "Blog Article"}
              </Title>

              <Text className="medium-article-dek">
                {blogByTitle?.description ?? articleDescription}
              </Text>

              <div className="medium-article-meta">
                <Group wrap="nowrap" gap="sm" align="center">
                  <Avatar radius="xl" color="dark" variant="filled">
                    SB
                  </Avatar>
                  <div className="medium-article-author-block">
                    <Text className="medium-article-author">
                      {BLOG_AUTHOR_NAME}
                    </Text>
                    <Text className="medium-article-publish">
                      Updated {publishedLabel} | {readingTime} min read
                    </Text>
                  </div>
                </Group>
              </div>

              {tagPreview.length ? (
                <div className="medium-article-tags">
                  {tagPreview.map((tag) => (
                    <span key={tag} className="medium-article-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </header>

            {shouldShowHeroImage ? (
              <div className="medium-article-hero-media">
                <img
                  className="medium-article-hero-image"
                  src={articleImage}
                  alt={articleImageAlt}
                  onError={() => setHasHeroImageError(true)}
                />
              </div>
            ) : null}

            <BlogPageSitelinks sectionLinks={articleContent.sectionLinks} />

            <Divider className="medium-article-divider" />

            <div className="medium-article-trust-section">
              <ReviewsTrustBar compact />
            </div>

            <Box className="medium-article-content">
              {parseHtml(articleContent.contentHtml)}
            </Box>

            {(relatedService || relatedBlogs.length) ? (
              <div className="medium-article-after">
                {relatedService ? (
                  <Paper
                    className="medium-sidebar-card medium-after-card"
                    radius="xl"
                    p={desktopMediaQuery ? "xl" : "lg"}
                    withBorder
                  >
                    <Stack gap="sm">
                      <Text className="medium-sidebar-eyebrow">Service page</Text>
                      <Title
                        order={desktopMediaQuery ? 3 : 4}
                        className="medium-sidebar-title"
                      >
                        {relatedService.heroTitle}
                      </Title>
                      <Text className="medium-sidebar-copy">
                        {relatedService.heroDescription}
                      </Text>
                      <Button
                        variant="subtle"
                        color="dark"
                        className="medium-sidebar-link"
                        component={Link}
                        to={`/services/${relatedService.slug}`}
                        rightSection={<IconArrowRight size={16} />}
                      >
                        View service page
                      </Button>
                    </Stack>
                  </Paper>
                ) : null}

                {relatedBlogs.length ? (
                  <section
                    className="medium-related-section"
                    aria-labelledby="suggested-blogs-heading"
                  >
                    <div className="medium-related-section-header">
                      <Text className="medium-sidebar-eyebrow">Keep reading</Text>
                      <Title
                        id="suggested-blogs-heading"
                        order={desktopMediaQuery ? 3 : 4}
                        className="medium-sidebar-title"
                      >
                        Suggested blogs
                      </Title>
                    </div>

                    <div className="medium-related-list">
                      {relatedBlogs.map((blog) => (
                        <Paper
                          key={blog.slug}
                          component={Link}
                          to={getBlogPath(blog.slug)}
                          className="medium-related-card"
                          radius="xl"
                          p={desktopMediaQuery ? "lg" : "md"}
                          withBorder
                        >
                          <Stack gap="xs">
                            <Text className="medium-related-kicker">
                              {blog.cluster}
                            </Text>
                            <Text className="medium-related-title">
                              {blog.title}
                            </Text>
                            <Text className="medium-sidebar-copy">
                              {blog.description}
                            </Text>
                            <Text className="medium-related-date">
                              {formatDate(blog.publishedOn)} |{" "}
                              {getReadingTimeMinutes(blog.body)} min read
                            </Text>
                          </Stack>
                        </Paper>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : null}

          </div>
        </article>

        <aside className="medium-article-rail">
          <div className="medium-article-rail-sticky">
            <Stack gap="md">
              <div className="medium-rail-intro">
                <Text className="medium-sidebar-eyebrow">Study support</Text>
                <Title
                  id="blog-help-form"
                  order={desktopMediaQuery ? 3 : 4}
                  className="medium-sidebar-title"
                >
                  Need Help With This?
                </Title>
                <Text className="medium-sidebar-copy">
                  Share your brief, module topic, or deadline for a clear next
                  step after reading the guide.
                </Text>
              </div>

              <Paper
                withBorder
                radius="xl"
                p={0}
                className="medium-sidebar-card medium-rail-form"
              >
                <GetHelpForm formSize="sm" flatForm />
              </Paper>
            </Stack>
          </div>
        </aside>
      </div>

      <div className="medium-article-footer">
        <AssignmentHelpHowItWorks
          className="medium-how-it-works"
          variant="flow"
          title="From Brief to Success: How SubjectBuddy Assignment Help Works"
        />
        <AssignmentSupportCta className="medium-assignment-cta" />
      </div>
    </Container>
  );
}

export default BlogViewer;
